import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '../services/api/users.api';
import { authApi } from '../services/api/auth.api';
import { User } from '../types/user.response';

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  authLoading: boolean;
  error: string | null;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const validateAuth = async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      setAuthLoading(false);
      return;
    }

    try {
      const userData = await usersApi.getUser();
      setUser(userData);
      setIsLoggedIn(true);
      setError(null);
    } catch (err) {
      console.error('Auth validation error:', err);
      setError('Authentication failed');
      setIsLoggedIn(false);
      setUser(null);
      localStorage.removeItem('token');
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    validateAuth();
  }, []);

  const refreshAuth = async () => {
    await validateAuth();
  };

  const loginWithGoogle = async (credential: string) => {
    const response = await authApi.googleLogin(credential);
    localStorage.setItem('token', response.accessToken);

    if (!response.profileComplete) {
      navigate('/complete-profile');
    } else {
      await validateAuth();
      navigate('/');
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, authLoading, error, logout, refreshAuth, loginWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}
